import type { ApiResponse } from '@jcy/common';

import { environment } from '../config/environment';
import {
  clearAdminSession,
  getAdminSession,
  setAdminSession,
  type AdminSession,
} from '../auth/session';

export interface ApiErrorDetail {
  readonly field?: unknown;
  readonly reason?: unknown;
}

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly details: readonly ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

function errorDetails(body: unknown): readonly ApiErrorDetail[] {
  const details = (body as { details?: unknown }).details;
  return Array.isArray(details) ? (details as readonly ApiErrorDetail[]) : [];
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return executeRequest<T>(path, init, true);
}

export interface ApiDownload {
  readonly blob: Blob;
  readonly filename: string;
  readonly sha256: string | null;
}

export async function apiDownload(path: string): Promise<ApiDownload> {
  return executeDownload(path, true);
}

async function executeRequest<T>(
  path: string,
  init: RequestInit | undefined,
  allowRefresh: boolean,
): Promise<T> {
  const requestId = crypto.randomUUID();
  const session = getAdminSession();
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const response = await fetch(`${environment.VITE_API_BASE_URL}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(isFormData ? {} : { 'content-type': 'application/json' }),
      'x-request-id': requestId,
      ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}),
      ...init?.headers,
    },
    credentials: 'include',
  });
  const body = await parseApiResponse<T>(response);
  if (!body) {
    throw new ApiClientError('HTTP_RESPONSE_INVALID', '服务暂不可用或响应格式无效', requestId);
  }
  if (!response.ok || !body.success) {
    if (!body.success) {
      if (allowRefresh && body.code === 'UNAUTHENTICATED' && session && path !== '/auth/refresh') {
        try {
          await refreshSession(session);
          return executeRequest<T>(path, init, false);
        } catch {
          if (getAdminSession()?.refreshToken === session.refreshToken) clearAdminSession();
        }
      }
      throw new ApiClientError(body.code, body.message, body.requestId, errorDetails(body));
    }
    throw new ApiClientError('HTTP_ERROR', '服务请求失败', requestId);
  }
  return body.data;
}

async function executeDownload(path: string, allowRefresh: boolean): Promise<ApiDownload> {
  const requestId = crypto.randomUUID();
  const session = getAdminSession();
  const response = await fetch(`${environment.VITE_API_BASE_URL}${path}`, {
    headers: {
      accept: 'application/pdf, application/json, application/zip',
      'x-request-id': requestId,
      ...(session ? { authorization: `Bearer ${session.accessToken}` } : {}),
    },
    credentials: 'include',
  });
  if (response.ok) {
    return {
      blob: await response.blob(),
      filename: responseFilename(response.headers.get('content-disposition')),
      sha256: response.headers.get('x-document-sha256'),
    };
  }
  const body = await parseApiResponse<never>(response);
  if (body && !body.success) {
    if (allowRefresh && body.code === 'UNAUTHENTICATED' && session) {
      try {
        await refreshSession(session);
        return executeDownload(path, false);
      } catch {
        if (getAdminSession()?.refreshToken === session.refreshToken) clearAdminSession();
      }
    }
    throw new ApiClientError(body.code, body.message, body.requestId, errorDetails(body));
  }
  throw new ApiClientError('HTTP_ERROR', '文件下载失败', requestId);
}

function responseFilename(contentDisposition: string | null): string {
  const encoded = /filename\*=UTF-8''([^;]+)/iu.exec(contentDisposition ?? '')?.[1];
  if (!encoded) return 'contract-draft.pdf';
  try {
    return decodeURIComponent(encoded);
  } catch {
    return 'contract-draft.pdf';
  }
}

let refreshInFlight: {
  readonly refreshToken: string;
  readonly promise: Promise<AdminSession>;
} | null = null;

async function refreshSession(staleSession: AdminSession): Promise<AdminSession> {
  const current = getAdminSession();
  if (current && current.refreshToken !== staleSession.refreshToken) return current;
  if (!refreshInFlight || refreshInFlight.refreshToken !== staleSession.refreshToken) {
    const promise = fetch(`${environment.VITE_API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-request-id': crypto.randomUUID() },
      body: JSON.stringify({ refreshToken: staleSession.refreshToken }),
    })
      .then(async (response) => {
        const body = await parseApiResponse<AdminSession>(response);
        if (!body) throw new Error('刷新登录状态响应无效');
        if (!response.ok || !body.success) throw new Error('刷新登录状态失败');
        if (getAdminSession()?.refreshToken === staleSession.refreshToken) {
          setAdminSession(body.data);
        }
        return body.data;
      })
      .finally(() => {
        if (refreshInFlight?.refreshToken === staleSession.refreshToken) refreshInFlight = null;
      });
    refreshInFlight = { refreshToken: staleSession.refreshToken, promise };
  }
  return refreshInFlight.promise;
}

async function parseApiResponse<T>(response: Response): Promise<ApiResponse<T> | null> {
  const text = await response.text();
  if (!text) return null;
  try {
    const value = JSON.parse(text) as unknown;
    if (typeof value !== 'object' || value === null || !('success' in value)) return null;
    return value as ApiResponse<T>;
  } catch {
    return null;
  }
}
